import { cn } from '../../lib/cn';

interface SkeletonProps {
  /** Width (CSS value or Tailwind class) */
  width?: string | number;
  /** Height (CSS value or Tailwind class) */
  height?: string | number;
  /** Shape variant */
  variant?: 'rectangle' | 'circle' | 'rounded';
  /** Make it circular (alias for variant="circle") */
  circle?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Skeleton loading placeholder with shimmer animation.
 */
export function Skeleton({
  width,
  height,
  variant = 'rectangle',
  circle = false,
  className,
}: SkeletonProps) {
  const style: React.CSSProperties = {};

  if (typeof width === 'number') {
    style.width = `${width}px`;
  } else if (width) {
    style.width = width;
  }

  if (typeof height === 'number') {
    style.height = `${height}px`;
  } else if (height) {
    style.height = height;
  }

  // Determine shape class
  const shapeClass = circle || variant === 'circle'
    ? 'rounded-full'
    : variant === 'rounded'
      ? 'rounded-lg'
      : 'rounded';

  return (
    <div
      className={cn('skeleton', shapeClass, className)}
      style={style}
      aria-hidden="true"
    />
  );
}

/**
 * Skeleton text line with realistic sizing.
 */
export function SkeletonText({
  lines = 1,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={16}
          className={cn(
            'w-full',
            // Last line is often shorter
            i === lines - 1 && lines > 1 && 'w-3/4'
          )}
        />
      ))}
    </div>
  );
}
